import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Flex,
  Group,
  NumberInput,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useClipboard, useElementSize } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft,
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconExternalLink,
  IconLink,
  IconSearch,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { useDocument, useDocumentHits, useNames, usePage } from "../../api/hooks";
import type { Fragment, SearchMode } from "../../api/types";
import { snippetToHtml } from "../../lib/snippet";
import { MetadataPanel } from "./metadata-panel";
import { PdfView } from "./pdf-view";
import { TextPageView } from "./text-page-view";

const MODES: SearchMode[] = ["hybrid", "fts", "semantic", "exact"];

export function DocumentPage() {
  const { t } = useTranslation();
  const { company: companyName, docType: docTypeName } = useNames();
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const q = params.get("q") ?? "";
  // The results page's query string (filters, mode, page) when the viewer was opened from it.
  const from = params.get("from") ?? "";
  const resultsUrl = from ? `/?${from}` : q ? `/?q=${encodeURIComponent(q)}` : "/documents";
  // Back: the results list this document was opened from (with its filters), else browser
  // history when we came from inside the app, else the archive. A login redirect also produces a
  // non-default history key, which is why the explicit results link comes first.
  const goBack = () => {
    if (from) navigate(resultsUrl);
    else if (location.key !== "default") navigate(-1);
    else navigate(resultsUrl);
  };
  const modeParam = params.get("mode");
  const mode: SearchMode = MODES.includes(modeParam as SearchMode)
    ? (modeParam as SearchMode)
    : "hybrid";
  const pageNo = Math.max(1, Number(params.get("page") ?? "1") || 1);

  const doc = useDocument(Number.isInteger(id) ? id : null);
  const hits = useDocumentHits(Number.isInteger(id) ? id : null, q, mode);
  const page = usePage(Number.isInteger(id) ? id : null, pageNo);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [text, setText] = useState(q);
  const { ref: frameRef, width: frameWidth } = useElementSize();
  const clipboard = useClipboard();

  useEffect(() => setText(q), [q]);
  // Only PdfView reports a page count; a DOCX (no PDF) or a cached card switch would otherwise
  // keep the previous document's count and clamp paging to it.
  useEffect(() => setNumPages(null), [id]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: key === "page" });
  };
  const goTo = (n: number) => {
    const max = numPages ?? doc.data?.pageCount ?? n;
    setParam("page", String(Math.min(Math.max(1, n), Math.max(1, max))));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setParam("q", text.trim());
  };

  const highlightTerms = useMemo(() => hits.data?.highlightTerms ?? [], [hits.data]);
  const fragmentsByPage = useMemo(() => {
    const map = new Map<number, Fragment[]>();
    for (const f of hits.data?.fragments ?? [])
      map.set(f.pageNo, [...(map.get(f.pageNo) ?? []), f]);
    return map;
  }, [hits.data]);
  const hitPages = [...fragmentsByPage.keys()].sort((a, b) => a - b);
  const prevHit = [...hitPages].reverse().find((p) => p < pageNo);
  const nextHit = hitPages.find((p) => p > pageNo);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement | null)?.tagName === "INPUT") return;
      if (e.key === "ArrowRight") goTo(pageNo + 1);
      if (e.key === "ArrowLeft") goTo(pageNo - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!Number.isInteger(id)) return <Alert color="red">{t("common.notFound")}</Alert>;
  if (doc.isError) {
    return (
      <Alert color="red" title={t("common.error")}>
        {(doc.error as Error).message}
      </Alert>
    );
  }
  if (!doc.data) return <Skeleton h={400} />;
  const d = doc.data;
  const fileUrl = `/api/documents/${d.id}/file`;

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Group gap="xs" align="flex-start" wrap="nowrap" style={{ flex: 1, minWidth: 260 }}>
          <Tooltip label={t("common.back")}>
            <ActionIcon
              variant="default"
              size="lg"
              aria-label={t("common.back")}
              onClick={goBack}
              style={{ flexShrink: 0, marginTop: 2 }}
            >
              <IconArrowLeft size={18} />
            </ActionIcon>
          </Tooltip>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Title order={3} lineClamp={2}>
              {d.title}
            </Title>
            <Group gap="xs">
              {d.docType && (
                <Badge variant="light">{docTypeName(d.docType.id) || d.docType.name_hy}</Badge>
              )}
              {d.regNumber && <Text size="sm">№ {d.regNumber}</Text>}
              {d.company && (
                <Text size="sm" c="dimmed">
                  {companyName(d.company.id) || d.company.name_hy}
                </Text>
              )}
            </Group>
          </Stack>
        </Group>
        <Group gap="xs">
          {d.hasFile && (
            <Button
              component="a"
              href={`/api/documents/${d.id}/original`}
              variant="default"
              size="xs"
              leftSection={<IconDownload size={14} />}
            >
              {t("document.download")}
            </Button>
          )}
          {d.mulberryUrl && (
            <Button
              component="a"
              href={d.mulberryUrl}
              target="_blank"
              rel="noreferrer"
              variant="default"
              size="xs"
              leftSection={<IconExternalLink size={14} />}
            >
              {t("document.openMulberry")}
            </Button>
          )}
          <Tooltip label={t("document.copyLinkHint")}>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconLink size={14} />}
              onClick={() => {
                clipboard.copy(window.location.href);
                notifications.show({ message: t("document.linkCopied"), color: "green" });
              }}
            >
              {t("document.copyLink")}
            </Button>
          </Tooltip>
        </Group>
      </Group>

      <Flex direction={{ base: "column", md: "row" }} align="stretch" gap="md">
        <Stack gap="sm" w={{ base: "100%", md: 320 }} style={{ flexShrink: 0 }}>
          <Paper withBorder p="sm">
            <form onSubmit={submit}>
              <TextInput
                size="sm"
                value={text}
                onChange={(e) => setText(e.currentTarget.value)}
                placeholder={t("document.searchInDoc")}
                leftSection={<IconSearch size={16} />}
              />
            </form>
            {q && (
              <Stack gap={4} mt="sm">
                <Group justify="space-between">
                  <Text size="sm" fw={600}>
                    {t("document.hits", { count: hits.data?.hitCount ?? 0 })}
                  </Text>
                  <Group gap={2}>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      disabled={prevHit === undefined}
                      onClick={() => prevHit !== undefined && goTo(prevHit)}
                      aria-label={t("document.prevHit")}
                    >
                      <IconChevronLeft size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      disabled={nextHit === undefined}
                      onClick={() => nextHit !== undefined && goTo(nextHit)}
                      aria-label={t("document.nextHit")}
                    >
                      <IconChevronRight size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
                <ScrollArea.Autosize mah={420} type="auto">
                  <Stack gap={6}>
                    {hits.isLoading && <Skeleton h={60} />}
                    {hitPages.map((p) =>
                      (fragmentsByPage.get(p) ?? []).map((f) => (
                        <Paper
                          key={f.chunkId}
                          withBorder
                          p={6}
                          onClick={() => goTo(p)}
                          style={{
                            cursor: "pointer",
                            borderColor: p === pageNo ? "var(--mantine-color-indigo-5)" : undefined,
                          }}
                        >
                          <Text size="xs" fw={600} c="indigo">
                            {t("search.page", { n: p })}
                            {f.clause ? ` · ${t("search.clause", { n: f.clause })}` : ""}
                          </Text>
                          <Text
                            size="xs"
                            lineClamp={3}
                            // snippetToHtml escapes everything except <mark>
                            dangerouslySetInnerHTML={{ __html: snippetToHtml(f.snippetHtml) }}
                          />
                        </Paper>
                      )),
                    )}
                    {hits.data && hits.data.hitCount === 0 && (
                      <Text size="xs" c="dimmed">
                        {t("search.none")}
                      </Text>
                    )}
                  </Stack>
                </ScrollArea.Autosize>
              </Stack>
            )}
          </Paper>
          <Paper withBorder p="sm">
            <MetadataPanel doc={d} />
          </Paper>
          {q && (
            <Anchor component={Link} to={resultsUrl} size="sm">
              ← {t("document.backToResults")}
            </Anchor>
          )}
        </Stack>

        <Stack gap="xs" style={{ flex: 1, minWidth: 0 }} className="viewer-column">
          <Paper withBorder p={6}>
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Group gap={4}>
                <ActionIcon
                  variant="default"
                  onClick={() => goTo(pageNo - 1)}
                  disabled={pageNo <= 1}
                  aria-label={t("document.prevPage")}
                >
                  <IconChevronLeft size={16} />
                </ActionIcon>
                <NumberInput
                  size="xs"
                  w={70}
                  min={1}
                  max={numPages ?? d.pageCount}
                  value={pageNo}
                  onChange={(v) => typeof v === "number" && goTo(v)}
                  hideControls
                />
                <Text size="sm">/ {numPages ?? d.pageCount}</Text>
                <ActionIcon
                  variant="default"
                  onClick={() => goTo(pageNo + 1)}
                  disabled={pageNo >= (numPages ?? d.pageCount)}
                  aria-label={t("document.nextPage")}
                >
                  <IconChevronRight size={16} />
                </ActionIcon>
                {page.data?.source === "ocr" && (
                  <Tooltip label={t("search.ocrHint", { count: 1 })}>
                    <Badge color="orange" variant="light" size="sm">
                      OCR
                    </Badge>
                  </Tooltip>
                )}
              </Group>
              <Group gap={4}>
                <ActionIcon
                  variant="default"
                  onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}
                  aria-label={t("document.zoomOut")}
                >
                  <IconZoomOut size={16} />
                </ActionIcon>
                <Text size="xs" w={40} ta="center">
                  {Math.round(scale * 100)}%
                </Text>
                <ActionIcon
                  variant="default"
                  onClick={() => setScale((s) => Math.min(3, s + 0.1))}
                  aria-label={t("document.zoomIn")}
                >
                  <IconZoomIn size={16} />
                </ActionIcon>
                <Button size="compact-xs" variant="subtle" onClick={() => setScale(1)}>
                  {t("document.fitWidth")}
                </Button>
              </Group>
            </Group>
          </Paper>
          <div ref={frameRef} style={{ width: "100%" }}>
            {d.fileProblem && (
              <Alert color="orange" variant="light" mb="xs">
                {t(`sources.fileProblem.${d.fileProblem}`, t("sources.fileUnavailable"))}
              </Alert>
            )}
            {!d.hasPdf ? (
              <TextPageView
                page={page.data}
                loading={page.isLoading}
                highlightTerms={highlightTerms}
              />
            ) : (
              <ScrollArea h={{ base: "70vh", md: "calc(100vh - 230px)" }} type="auto">
                {frameWidth > 0 && (
                  <PdfView
                    fileUrl={fileUrl}
                    pageNo={pageNo}
                    width={frameWidth - 16}
                    scale={scale}
                    highlightTerms={highlightTerms}
                    words={page.data?.source === "ocr" ? page.data.words : null}
                    legacy={page.data?.source === "legacy_map"}
                    onLoaded={setNumPages}
                  />
                )}
              </ScrollArea>
            )}
          </div>
        </Stack>
      </Flex>
    </Stack>
  );
}
